import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { useState, useMemo, useCallback, useRef, memo } from "react";
import { useAppSelector } from "@stores/redux/store";
import { useChatStore } from "@stores/chatStore";
import {
  TagsOutlined,
  MoreOutlined,
  PushpinOutlined,
  // DeleteOutlined,
  UserOutlined,
  CheckOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  markConversationAsDone,
  unmarkConversationAsDone,
} from "@api/services/facebook";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "../styles.module.less";
import type { ConversationItemMenuProps } from "./types";
import { useMenuHandlers } from "./hooks/useMenuHandlers";
import { useTagSubmenu } from "./hooks/useTagSubmenu";
import { useAgentSubmenu } from "./hooks/useAgentSubmenu";

const ItemOption = memo(
  ({ icon, label }: { icon: React.ReactNode; label: string }) => {
    return (
      <div className={styles.menuItemLabel}>
        <div className={styles.menuItemLabelInner}>
          {icon}
          <span className={styles.menuItemText}>{label}</span>
        </div>
      </div>
    );
  }
);

ItemOption.displayName = "ItemOption";

export const ConversationItemMenu = ({
  conversation,
  tags,
  agent,
  selectedPageId,
  folderStatus,
  onPin,
  onDelete,
  onTagsChange,
  onAgentChange,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ConversationItemMenuProps) => {
  const { success, error: showError } = useNotificationContext();
  const updateConversationFolderStatus = useChatStore(
    (state) => state.updateConversationFolderStatus
  );
  const [submenuOpenKeys, setSubmenuOpenKeys] = useState<string[]>([]);
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const dropdownTriggerRef = useRef<HTMLSpanElement>(null);
  const keepSubmenuOpenRef = useRef<{ agent?: boolean; tag?: boolean }>({});
  const [markingAsDone, setMarkingAsDone] = useState(false);

  const { tags: availableTags, loading: loadingTags } = useAppSelector(
    (state) => state.tags
  );

  const { agents, loading: loadingAgents } = useAppSelector(
    (state) => state.agents
  );

  const { handleTagToggle, handleAgentSelect, handleAgentRemove } =
    useMenuHandlers({
      conversation,
      tags,
      agent,
      selectedPageId,
      availableTags,
      agents,
      onTagsChange,
      onAgentChange,
    });

  const tagSubmenuItems = useTagSubmenu({
    availableTags,
    loadingTags,
    tags,
    onTagToggle: handleTagToggle,
  });

  const agentSubmenuItems = useAgentSubmenu({
    agents,
    loadingAgents,
    selectedAgent: agent,
    conversationId: conversation.lastMessage?.conversationId,
    onAgentSelect: handleAgentSelect,
    onAgentRemove: handleAgentRemove,
  });

  const dropdownOpen =
    controlledOpen !== undefined ? controlledOpen : internalDropdownOpen;
  const setDropdownOpen = controlledOnOpenChange || setInternalDropdownOpen;

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      setDropdownOpen(open);
      if (!open) {
        setSubmenuOpenKeys([]);
      }
    },
    [setDropdownOpen]
  );

  const handleMarkAsDone = useCallback(async () => {
    if (!conversation?.lastMessage?.conversationId) {
      showError("Không tìm thấy conversation");
      return;
    }

    if (!selectedPageId) {
      showError("Vui lòng chọn fanpage trước khi đánh dấu xong");
      return;
    }

    const conversationId = conversation.lastMessage.conversationId;

    try {
      setMarkingAsDone(true);
      const response = await markConversationAsDone({
        conversationId,
        pageId: selectedPageId,
      });

      if (response.code === 0 || response.code === 200) {
        success("Đánh dấu xong thành công");
        if (conversationId) {
          updateConversationFolderStatus({
            [conversationId]: "DONE",
          });
        }

        const triggerRefresh =
          useChatStore.getState().triggerRefreshConversationHistory;
        triggerRefresh();
      } else {
        showError(response.message || "Đánh dấu xong thất bại");
      }
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Không thể đánh dấu xong. Vui lòng thử lại."
      );
    } finally {
      setMarkingAsDone(false);
    }
  }, [
    conversation,
    selectedPageId,
    success,
    showError,
    updateConversationFolderStatus,
  ]);

  const handleUnmarkAsDone = useCallback(async () => {
    if (!conversation?.lastMessage?.conversationId) {
      showError("Không tìm thấy conversation");
      return;
    }

    if (!selectedPageId) {
      showError("Vui lòng chọn fanpage trước khi bỏ đánh dấu xong");
      return;
    }

    const conversationId = conversation.lastMessage.conversationId;

    try {
      setMarkingAsDone(true);
      const response = await unmarkConversationAsDone({
        conversationId,
        pageId: selectedPageId,
      });

      if (response.code === 0 || response.code === 200) {
        success("Bỏ đánh dấu xong thành công");
        if (conversationId) {
          updateConversationFolderStatus({
            [conversationId]: "",
          });
        }

        const triggerRefresh =
          useChatStore.getState().triggerRefreshConversationHistory;
        triggerRefresh();
      } else {
        showError(response.message || "Bỏ đánh dấu xong thất bại");
      }
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Không thể bỏ đánh dấu xong. Vui lòng thử lại."
      );
    } finally {
      setMarkingAsDone(false);
    }
  }, [
    conversation,
    selectedPageId,
    success,
    showError,
    updateConversationFolderStatus,
  ]);

  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      if (key.startsWith("tag-")) {
        const tagId = key.replace("tag-", "");
        handleTagToggle(tagId);
        keepSubmenuOpenRef.current.tag = true;
        setSubmenuOpenKeys((prev) => {
          if (!prev.includes("tag")) {
            return [...prev, "tag"];
          }
          return prev;
        });
        return false;
      }

      if (key.startsWith("agent-")) {
        const agentId = parseInt(key.replace("agent-", ""), 10);
        handleAgentSelect(agentId);
        keepSubmenuOpenRef.current.agent = true;
        setSubmenuOpenKeys((prev) => {
          if (!prev.includes("agent")) {
            return [...prev, "agent"];
          }
          return prev;
        });
        return false;
      }

      switch (key) {
        case "pin":
          onPin();
          break;
        case "tag":
          break;
        case "agent":
          break;
        case "mark-done":
          handleMarkAsDone();
          break;
        case "unmark-done":
          handleUnmarkAsDone();
          break;
        case "delete":
          onDelete?.();
          break;
      }
    },
    [
      onPin,
      onDelete,
      handleTagToggle,
      handleAgentSelect,
      handleMarkAsDone,
      handleUnmarkAsDone,
    ]
  );

  const menuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "pin",
        label: conversation.isPinned ? "Bỏ ghim" : "Ghim",
        icon: <PushpinOutlined />,
      },
      {
        key: "tag",
        label: <ItemOption icon={<TagsOutlined />} label="Gắn thẻ" />,
        children: tagSubmenuItems,
      },
      {
        key: "agent",
        label: <ItemOption icon={<UserOutlined />} label="Gắn Agent" />,
        children: agentSubmenuItems,
      },
      {
        type: "divider",
      },
      ...(folderStatus === "DONE"
        ? [
            {
              key: "unmark-done",
              label: "Bỏ đánh dấu xong",
              icon: <CloseCircleOutlined />,
              disabled:
                markingAsDone ||
                !conversation?.lastMessage?.conversationId ||
                !selectedPageId,
            },
          ]
        : [
            {
              key: "mark-done",
              label: "Đánh dấu xong",
              icon: <CheckOutlined />,
              disabled:
                markingAsDone ||
                !conversation?.lastMessage?.conversationId ||
                !selectedPageId,
            },
          ]),
      // {
      //   type: "divider",
      // },
      // {
      //   key: "delete",
      //   label: "Xóa",
      //   icon: <DeleteOutlined />,
      //   danger: true,
      // },
    ],
    [
      conversation.isPinned,
      conversation?.lastMessage?.conversationId,
      selectedPageId,
      markingAsDone,
      tagSubmenuItems,
      agentSubmenuItems,
      folderStatus,
    ]
  );

  const getPopupContainer = useCallback(() => {
    return document.body;
  }, []);

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: handleMenuClick,
        selectable: false,
        triggerSubMenuAction: "hover",
        openKeys: submenuOpenKeys,
        onOpenChange: (keys) => {
          const openKeysArray = keys as string[];
          if (
            keepSubmenuOpenRef.current.agent &&
            !openKeysArray.includes("agent")
          ) {
            openKeysArray.push("agent");
          }
          if (
            keepSubmenuOpenRef.current.tag &&
            !openKeysArray.includes("tag")
          ) {
            openKeysArray.push("tag");
          }
          if (
            keepSubmenuOpenRef.current.agent &&
            openKeysArray.includes("agent")
          ) {
            keepSubmenuOpenRef.current.agent = false;
          }
          if (keepSubmenuOpenRef.current.tag && openKeysArray.includes("tag")) {
            keepSubmenuOpenRef.current.tag = false;
          }

          setSubmenuOpenKeys(openKeysArray);
        },
      }}
      trigger={["click", "contextMenu"]}
      placement="bottomRight"
      overlayClassName={styles.dropdownMenu}
      getPopupContainer={getPopupContainer}
      onOpenChange={handleDropdownOpenChange}
      destroyOnHidden={true}
      open={dropdownOpen}
    >
      <span
        ref={dropdownTriggerRef}
        className={styles.moreButtonWrapper}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <MoreOutlined className={styles.moreButton} />
      </span>
    </Dropdown>
  );
};
